/**
 * Layout principal para área do Tenant
 * Inclui sidebar, header com notificações e avatar do usuário
 * Exibe paywall quando tenant está suspenso (exceto página de planos)
 * Suporta alternância entre módulos (Conversas/CRM)
 */
import { ReactNode, useState } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
// Navegação 2.0 — rail de categorias + painel de submenus (substitui sidebars por módulo)
import { AppShellSidebar } from '@/components/navigation/AppShellSidebar';

import { useAuth } from '@/hooks/useAuth';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { ThemeToggle } from '@/components/ui/theme-toggle';

import { TooltipProvider } from '@/components/ui/tooltip';
import { UserProfileDialog } from '@/components/tenant/UserProfileDialog';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
import { TrialBanner } from '@/components/tenant/TrialBanner';
import { PaymentGuard } from '@/components/guards/PaymentGuard';
// Hook do novo modelo de planos (integracao_v1) — usado para suprimir o
// TrialBanner legado quando o tenant já está vinculado à nova estrutura.
import { useTenantBillingSelfOverview } from '@/hooks/tenant/useTenantPlansV2';

import { NotificationBell } from '@/components/tenant/notifications/NotificationBell';
import { AppLauncherButton } from '@/components/shortcuts/AppLauncherButton';
import { QuickCreateButton } from '@/components/quick-create/QuickCreateButton';
// Bloco central compartilhado do header (pesquisa global + botão "+")
import { HeaderSearchSlot } from '@/components/layouts/HeaderSearchSlot';

import { DisconnectedInboxesIndicator } from '@/components/tenant/inbox/DisconnectedInboxesIndicator';
import { HeaderOverflowMenu } from '@/components/tenant/HeaderOverflowMenu';

import { UserMenu } from '@/components/tenant/UserMenu';
// v1.x — Switcher para painel WL quando usuário tem cruzamento WL × tenant
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Settings2 } from 'lucide-react';
import { useIsWlUser } from '@/hooks/useIsWlUser';
import { useWlInternalTenantIdForHeader } from '@/hooks/wl/useWlInternalTenantIdForHeader';
import { clearLastContextChoice } from '@/lib/contextSwitcher';


interface TenantLayoutProps {
  children: ReactNode;
  /**
   * Quando true, o shell trava em 100vh e o <main> não rola — a página filha
   * assume 100% de altura e gerencia seu próprio scroll (usado por telas
   * estilo GitBook: KB com sidebars fixas e coluna central rolável).
   * Default: false (mantém comportamento legado com scroll de janela).
   */
  fullHeight?: boolean;
}

/**
 * Extrai as iniciais do nome ou email do usuário
 * @param name - Nome completo ou email
 * @returns Iniciais em maiúsculo (máximo 2 caracteres)
 */
const getInitials = (name: string): string => {
  if (!name) return 'U';
  
  // Se for email, usa primeira letra antes do @
  if (name.includes('@')) {
    return name.split('@')[0].charAt(0).toUpperCase();
  }
  
  // Se for nome, pega as iniciais das primeiras duas palavras
  return name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Obtém a role principal do usuário no tenant atual
 */
const getUserRole = (roles: { role: string; tenant_id: string | null }[], tenantId?: string): 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user' => {
  if (!tenantId) return 'user';
  
  // Prioridade: root_admin > tenant_admin > tenant_supervisor > agent > user
  if (roles.some(r => r.role === 'root_admin' && r.tenant_id === null)) return 'root_admin';
  if (roles.some(r => r.role === 'tenant_admin' && r.tenant_id === tenantId)) return 'tenant_admin';
  if (roles.some(r => r.role === 'tenant_supervisor' && r.tenant_id === tenantId)) return 'tenant_supervisor';
  if (roles.some(r => r.role === 'agent' && r.tenant_id === tenantId)) return 'agent';
  return 'user';
};

export function TenantLayout({ children, fullHeight = false }: TenantLayoutProps) {
  const { user, signOut, currentTenant, availableTenants, switchTenant, profile, refreshProfile, roles } = useAuth();
  // v1.1.0 — Detecta se o tenant já está no novo modelo de planos (integracao_v1)
  // para suprimir o TrialBanner legado (que ainda lê o enum `tenants.plan`).
  // O cache é compartilhado com o PaymentGuard via React Query, sem custo extra.
  const { data: billingOverview } = useTenantBillingSelfOverview();
  const hasIntegracaoV1Binding =
    !!billingOverview?.has_binding &&
    (billingOverview?.gateway_slug ?? 'uaizy') === 'uaizy';
  const navigate = useNavigate();
  const { isWlUser } = useIsWlUser();
  // Busca o UUID do tenant interno do WL via RPC dedicada ao header
  // (`wl_get_internal_tenant_id_for_header`), cujo gate é `wl.dashboard:view`.
  // Assim, qualquer operador WL com acesso ao Dashboard do painel WL vê o
  // botão — sem depender de `wl.settings:view` (usado apenas na tela de
  // Configurações → Tenant interno).
  const { internalTenantId } = useWlInternalTenantIdForHeader({ enabled: isWlUser });
  const isInternalTenant =
    !!internalTenantId &&
    !!currentTenant?.id &&
    internalTenantId === currentTenant.id;
  const hasCrossover = isWlUser && isInternalTenant;

  const goToWl = () => {
    clearLastContextChoice();
    navigate('/wl', { replace: true });
  };


  // FONTE ÚNICA DE VERDADE para flags de módulo no header (inclui Indicação).
  // O objeto `moduleSwitcherProps` deve ser espalhado em <ModuleSwitcher /> e
  // <HeaderOverflowMenu /> — garante consistência entre todos os layouts.
  const { moduleSwitcherProps } = useHeaderModuleFlags();
  
  // Estado do modal de perfil
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  
  // Role do usuário no tenant atual
  const userRole = getUserRole(roles, currentTenant?.id);

  /**
   * Executa logout. O redirecionamento (hard reload para /auth) é
   * feito internamente pelo signOut do AuthProvider, que também
   * coordena via BroadcastChannel para todas as abas abertas.
   */
  const handleLogout = () => {
    signOut();
  };




  return (
    <PaymentGuard>
    <TooltipProvider>
      <SidebarProvider defaultOpen={true}>
        <div className={fullHeight ? "app-viewport flex w-full overflow-hidden" : "app-viewport-min flex w-full"}>
          <AppShellSidebar />
          {/* 
           * CONTENÇÃO FLEXBOX: min-w-0 é OBRIGATÓRIO aqui!
           * Em Flexbox, itens flex têm min-width: auto por padrão, permitindo que
           * conteúdo interno (ex: Kanban com muitas colunas) expanda o container.
           * min-w-0 permite que o item encolha e crie overflow interno.
           */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header com margens e padding responsivos para mobile */}
            <header className="h-12 sm:h-14 bg-card flex items-center gap-2 px-2 sm:px-4 mx-2 sm:mx-4 md:mx-6 mt-2 sm:mt-4 rounded-lg shadow-sm border">
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink-0">
                <SidebarTrigger />
                <TenantSwitcher
                  availableTenants={availableTenants}
                  currentTenant={currentTenant}
                  onSwitch={switchTenant}
                />
                {/* Indicador de Inboxes Desconectadas - apenas xl+ */}
                <div className="hidden xl:block">
                  <DisconnectedInboxesIndicator />
                </div>
              </div>

              {/* Bloco central: busca global + botão "+" */}
              <HeaderSearchSlot />

              {/* Espaçador no mobile */}
              <div className="flex-1 md:hidden" />

              {/* Banner de Trial - apenas 2xl+ (evita disputar espaço com a busca) */}
              <div className="hidden 2xl:flex items-center gap-4 flex-shrink-0">
                <TrialBanner
                  trialEndsAt={currentTenant?.trial_ends_at || null}
                  planName={currentTenant?.plan || null}
                  tenantStatus={currentTenant?.status || null}
                  hideForIntegracaoV1={hasIntegracaoV1Binding}
                />
              </div>
              

              
              {/* Ações do Header */}
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {/* Botão direto para painel WL — visível só se houver cruzamento real */}
                {hasCrossover && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 hidden md:inline-flex"
                    onClick={goToWl}
                  >
                    <Settings2 className="h-4 w-4" />
                    <span className="hidden lg:inline">Painel WL</span>
                  </Button>
                )}


                {/* Menu overflow COMPLETO - visível apenas mobile (<768px) */}
                <div className="md:hidden">
                  <HeaderOverflowMenu {...moduleSwitcherProps} variant="full" />
                </div>

                {/* Menu overflow AÇÕES - visível desktop compacto (768px–1280px) */}
                <div className="hidden md:block xl:hidden">
                  <HeaderOverflowMenu {...moduleSwitcherProps} variant="actions-only" />
                </div>
                
                {/* Status do Agente - apenas xl+ */}
                
                
                {/* Toggle de Tema - apenas xl+ */}
                <div className="hidden xl:block">
                  <ThemeToggle />
                </div>
                
                {/* Notificações - sempre visível */}
                <AppLauncherButton />
                {/* No md+ o "+" já aparece ao lado da busca central */}
                <div className="md:hidden">
                  <QuickCreateButton />
                </div>

                <NotificationBell />
                
                {/* Menu de Usuário - sempre visível */}
                <UserMenu
                  user={user}
                  profile={profile}
                  userRole={userRole}
                  onOpenProfile={() => setProfileDialogOpen(true)}
                  onLogout={handleLogout}
                />
              </div>
            </header>
            {/* 
             * Main Content - min-w-0 e overflow-x-hidden garantem que páginas 
             * com conteúdo largo (Kanban, tabelas) não expandam o layout horizontalmente.
             * O scroll horizontal deve ser interno ao componente (ex: ScrollArea).
             */}
            <main
              className={
                fullHeight
                  ? "flex-1 min-w-0 min-h-0 overflow-hidden bg-muted/30"
                  : "flex-1 min-w-0 overflow-x-hidden p-3 sm:p-4 md:p-6 bg-muted/30"
              }
            >
              {children}
            </main>
          </div>
        </div>
        
        {/* Modal de Perfil do Usuário */}
        <UserProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          userId={user?.id || ''}
          userEmail={user?.email || ''}
          currentName={profile?.full_name || null}
          currentAvatarPath={profile?.avatar_url || null}
          onSuccess={refreshProfile}
        />
        
    </SidebarProvider>
    </TooltipProvider>
    </PaymentGuard>
  );
}
